import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, Phone, Mail, MessageSquare, Video, Bell, CheckCircle2, 
  XCircle, Clock, Calendar, Plus, Filter, Download, Printer, RefreshCw,
  User, Building2, Package, DollarSign, Globe, History, ChevronDown,
  ChevronRight, Send, FileText, AlertTriangle, Target, RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import { formatDMY } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Activity type configurations
const ACTIVITY_TYPE_CONFIG = {
  call: { label: "Phone Call", icon: Phone, color: "bg-blue-100 text-blue-700" },
  email: { label: "Email", icon: Mail, color: "bg-purple-100 text-purple-700" },
  whatsapp: { label: "WhatsApp", icon: MessageSquare, color: "bg-green-100 text-green-700" },
  meeting: { label: "Meeting", icon: Video, color: "bg-orange-100 text-orange-700" },
  note: { label: "Note", icon: FileText, color: "bg-gray-100 text-gray-700" },
  follow_up: { label: "Follow-up", icon: Bell, color: "bg-yellow-100 text-yellow-700" },
  follow_up_scheduled: { label: "Scheduled", icon: Calendar, color: "bg-indigo-100 text-indigo-700" },
  follow_up_completed: { label: "Completed", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  follow_up_rescheduled: { label: "Rescheduled", icon: RotateCcw, color: "bg-amber-100 text-amber-700" },
  follow_up_cancelled: { label: "Cancelled", icon: XCircle, color: "bg-red-100 text-red-700" }
};

// Outcome configurations
const OUTCOME_CONFIG = {
  connected: { label: "Connected", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  no_answer: { label: "No Answer", color: "bg-yellow-100 text-yellow-700", icon: Phone },
  voicemail: { label: "Left Voicemail", color: "bg-blue-100 text-blue-700", icon: MessageSquare },
  meeting_done: { label: "Meeting Done", color: "bg-purple-100 text-purple-700", icon: Video },
  order_placed: { label: "Order Placed", color: "bg-emerald-100 text-emerald-700", icon: Package },
  not_interested: { label: "Not Interested", color: "bg-red-100 text-red-700", icon: XCircle },
  wrong_number: { label: "Wrong Number", color: "bg-gray-100 text-gray-700", icon: XCircle },
  callback_requested: { label: "Callback Requested", color: "bg-orange-100 text-orange-700", icon: RotateCcw },
  positive: { label: "Positive", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  neutral: { label: "Neutral", color: "bg-gray-100 text-gray-700", icon: Target },
  negative: { label: "Negative", color: "bg-red-100 text-red-700", icon: XCircle }
};

const ClientTimelinePage = () => {
  const { token } = useAuth();
  const { clientCode } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState([]);
  const [stats, setStats] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [expandedThreads, setExpandedThreads] = useState({});
  
  // Add Note modal
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({ content: "", type: "note" });
  
  // Schedule Follow-up modal
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpForm, setFollowUpForm] = useState({
    follow_up_date: "",
    follow_up_type: "call",
    notes: "",
    priority: "medium"
  });

  // Fetch timeline data
  const fetchTimeline = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/client/${clientCode}/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTimeline(data.timeline || []);
        setStats(data.stats);
      } else {
        toast.error("Failed to load timeline");
      }
    } catch (error) {
      console.error("Error fetching timeline:", error);
      toast.error("Failed to load timeline");
    } finally {
      setLoading(false);
    }
  };

  // Fetch client info
  const fetchClientInfo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/client/${clientCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClientInfo(data);
      }
    } catch (error) {
      console.error("Error fetching client info:", error);
    }
  };

  useEffect(() => {
    if (clientCode) {
      fetchTimeline();
      fetchClientInfo();
    }
  }, [clientCode]);

  // Add note
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteForm.content.trim()) {
      toast.error("Please enter a note");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/activity`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          client_code: clientCode,
          activity_type: noteForm.type,
          description: noteForm.content,
          outcome: "neutral"
        })
      });
      
      if (response.ok) {
        toast.success("Note added successfully");
        setShowAddNoteModal(false);
        setNoteForm({ content: "", type: "note" });
        fetchTimeline();
      } else {
        toast.error("Failed to add note");
      }
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  // Schedule follow-up
  const handleScheduleFollowUp = async (e) => {
    e.preventDefault();
    if (!followUpForm.follow_up_date) {
      toast.error("Please select a date");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/follow-up`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          client_code: clientCode,
          ...followUpForm
        })
      });
      
      if (response.ok) {
        toast.success("Follow-up scheduled");
        setShowFollowUpModal(false);
        setFollowUpForm({ follow_up_date: "", follow_up_type: "call", notes: "", priority: "medium" });
        fetchTimeline();
      } else {
        toast.error("Failed to schedule follow-up");
      }
    } catch (error) {
      toast.error("Failed to schedule follow-up");
    }
  };

  // Filter timeline
  const filteredTimeline = timeline.filter(item => {
    if (filterType === "all") return true;
    if (filterType === "follow_ups") return item.type === "follow_up";
    if (filterType === "activities") return item.type === "activity";
    if (filterType === "calls") return item.activity_type === "call" || item.follow_up_type === "call";
    if (filterType === "emails") return item.activity_type === "email" || item.follow_up_type === "email";
    if (filterType === "meetings") return item.activity_type === "meeting" || item.follow_up_type === "meeting";
    return true;
  });

  // Group timeline by threads (linked follow-ups)
  const groupedTimeline = filteredTimeline.reduce((acc, item) => {
    // If this is a child follow-up, find its parent thread
    if (item.parent_follow_up_id) {
      const parentIdx = acc.findIndex(group => 
        group.items.some(i => i.id === item.parent_follow_up_id)
      );
      if (parentIdx !== -1) {
        acc[parentIdx].items.push(item);
        return acc;
      }
    }
    
    // Start a new group
    acc.push({ 
      id: item.id || `group-${acc.length}`,
      items: [item],
      date: item.date
    });
    return acc;
  }, []);

  // Toggle thread expansion
  const toggleThread = (groupId) => {
    setExpandedThreads(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Print timeline
  const handlePrint = () => {
    window.print();
  };

  // Export to CSV
  const handleExport = () => {
    const headers = ["Date", "Type", "Description", "Outcome", "Created By"];
    const rows = timeline.map(item => [
      item.date?.split("T")[0] || "",
      item.type === "follow_up" ? item.follow_up_type : item.activity_type,
      item.type === "follow_up" ? item.notes : item.description,
      item.outcome || "",
      item.created_by || ""
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${clientCode}_timeline.csv`;
    a.click();
  };

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen print:bg-white print:p-4">
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/crm")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to CRM
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <History className="w-7 h-7 text-blue-600" />
              Client Timeline
            </h1>
            <p className="text-slate-500 text-sm">Complete interaction history for {clientCode}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-1" /> Export
          </Button>
          <Button variant="outline" size="sm" onClick={fetchTimeline}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block mb-6">
        <h1 className="text-xl font-bold">Client Timeline: {clientCode}</h1>
        <p className="text-sm text-gray-600">Generated on {formatDMY(new Date())}</p>
      </div>

      {/* Client Summary Card */}
      {clientInfo && (
        <Card className="print:shadow-none print:border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xl">
                  {clientCode.substring(0, 2)}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{clientCode}</h2>
                  {clientInfo.ledger?.name && (
                    <p className="text-slate-500">{clientInfo.ledger.name}</p>
                  )}
                  <div className="flex items-center gap-4 mt-1 text-sm text-slate-600">
                    {clientInfo.ledger?.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {clientInfo.ledger.email}
                      </span>
                    )}
                    {clientInfo.ledger?.phone_number && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {clientInfo.ledger.phone_number}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="flex items-center gap-6 print:hidden">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{stats?.total_follow_ups || 0}</p>
                  <p className="text-xs text-slate-500">Total Follow-ups</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{stats?.completed_follow_ups || 0}</p>
                  <p className="text-xs text-slate-500">Completed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">{stats?.pending_follow_ups || 0}</p>
                  <p className="text-xs text-slate-500">Pending</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{stats?.total_activities || 0}</p>
                  <p className="text-xs text-slate-500">Activities</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions & Filters */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowAddNoteModal(true)} variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Add Note
          </Button>
          <Button onClick={() => setShowFollowUpModal(true)} size="sm">
            <Calendar className="w-4 h-4 mr-1" /> Schedule Follow-up
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activities</SelectItem>
              <SelectItem value="follow_ups">Follow-ups Only</SelectItem>
              <SelectItem value="activities">Activities Only</SelectItem>
              <SelectItem value="calls">Calls</SelectItem>
              <SelectItem value="emails">Emails</SelectItem>
              <SelectItem value="meetings">Meetings</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Timeline */}
      <Card className="print:shadow-none print:border-0">
        <CardHeader className="pb-2 print:hidden">
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5 text-blue-600" />
            Activity Timeline
            <Badge variant="outline" className="ml-2">{filteredTimeline.length} items</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-500" />
            </div>
          ) : groupedTimeline.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <History className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No activity history found</p>
              <Button onClick={() => setShowFollowUpModal(true)} className="mt-4" size="sm">
                <Plus className="w-4 h-4 mr-1" /> Schedule First Follow-up
              </Button>
            </div>
          ) : (
            <div className="relative px-6 pb-6">
              {/* Timeline line */}
              <div className="absolute left-10 top-0 bottom-0 w-0.5 bg-slate-200 print:bg-gray-300"></div>
              
              {groupedTimeline.map((group, groupIdx) => (
                <div key={group.id} className="relative">
                  {group.items.map((item, itemIdx) => {
                    const isFollowUp = item.type === "follow_up";
                    const activityType = isFollowUp ? item.follow_up_type : item.activity_type;
                    const typeConfig = ACTIVITY_TYPE_CONFIG[activityType] || ACTIVITY_TYPE_CONFIG.note;
                    const TypeIcon = typeConfig.icon;
                    const outcomeConfig = item.outcome ? OUTCOME_CONFIG[item.outcome] : null;
                    const isThread = group.items.length > 1;
                    const isFirstInThread = itemIdx === 0;
                    const isExpanded = expandedThreads[group.id];
                    
                    // Hide non-first items in collapsed threads
                    if (isThread && !isFirstInThread && !isExpanded) return null;
                    
                    return (
                      <div key={item.id || `${groupIdx}-${itemIdx}`} className={`relative pl-12 pb-6 ${itemIdx > 0 ? 'ml-6 border-l-2 border-dashed border-blue-200' : ''}`}>
                        {/* Timeline dot */}
                        <div className={`absolute left-6 w-8 h-8 rounded-full flex items-center justify-center ${typeConfig.color} print:border print:border-gray-400`}>
                          <TypeIcon className="w-4 h-4" />
                        </div>
                        
                        {/* Thread indicator */}
                        {isThread && isFirstInThread && (
                          <button 
                            onClick={() => toggleThread(group.id)}
                            className="absolute left-14 top-1 text-xs text-blue-600 hover:underline flex items-center gap-1 print:hidden"
                          >
                            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            {group.items.length - 1} linked {group.items.length === 2 ? 'follow-up' : 'follow-ups'}
                          </button>
                        )}
                        
                        <div className={`bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow print:shadow-none print:border-gray-300 ${isThread && isFirstInThread ? 'mt-6' : ''}`}>
                          {/* Header */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`text-xs ${typeConfig.color}`}>
                                {typeConfig.label}
                              </Badge>
                              {isFollowUp && item.status && (
                                <Badge className={`text-xs ${
                                  item.status === "completed" ? "bg-green-100 text-green-700" :
                                  item.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                                  item.status === "cancelled" ? "bg-red-100 text-red-700" :
                                  "bg-gray-100 text-gray-700"
                                }`}>
                                  {item.status}
                                </Badge>
                              )}
                              {outcomeConfig && (
                                <Badge className={`text-xs ${outcomeConfig.color}`}>
                                  {outcomeConfig.label}
                                </Badge>
                              )}
                              {isFollowUp && item.priority && (
                                <Badge variant="outline" className={`text-xs ${
                                  item.priority === "urgent" ? "border-red-500 text-red-600" :
                                  item.priority === "high" ? "border-orange-500 text-orange-600" :
                                  item.priority === "medium" ? "border-yellow-500 text-yellow-600" :
                                  "border-green-500 text-green-600"
                                }`}>
                                  {item.priority}
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-slate-400">
                              {item.date?.split("T")[0]}
                              {item.date?.includes("T") && ` ${item.date.split("T")[1]?.substring(0, 5)}`}
                            </span>
                          </div>
                          
                          {/* Content */}
                          <div className="text-sm text-slate-700">
                            {isFollowUp ? (
                              <>
                                {item.follow_up_date && (
                                  <p className="flex items-center gap-1 mb-1">
                                    <Calendar className="w-3 h-3 text-slate-400" />
                                    <span className="font-medium">Scheduled:</span> {item.follow_up_date}
                                  </p>
                                )}
                                {item.notes && <p>{item.notes}</p>}
                                {item.completion_notes && (
                                  <p className="mt-2 p-2 bg-green-50 rounded text-green-700 text-xs">
                                    <strong>Completion Notes:</strong> {item.completion_notes}
                                  </p>
                                )}
                              </>
                            ) : (
                              <p>{item.description}</p>
                            )}
                          </div>
                          
                          {/* Footer */}
                          <div className="flex items-center justify-between mt-3 pt-2 border-t text-xs text-slate-400">
                            <span>By: {item.created_by || "System"}</span>
                            {item.reschedule_count > 0 && (
                              <span className="text-amber-600">Rescheduled {item.reschedule_count}x</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Note Modal */}
      <Dialog open={showAddNoteModal} onOpenChange={setShowAddNoteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Add Note for {clientCode}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddNote} className="space-y-4">
            <div className="space-y-2">
              <Label>Activity Type</Label>
              <Select value={noteForm.type} onValueChange={(v) => setNoteForm(prev => ({ ...prev, type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">General Note</SelectItem>
                  <SelectItem value="call">Phone Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Content *</Label>
              <Textarea
                value={noteForm.content}
                onChange={(e) => setNoteForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Enter your note here..."
                rows={4}
                required
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddNoteModal(false)}>Cancel</Button>
              <Button type="submit">
                <Plus className="w-4 h-4 mr-1" /> Add Note
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedule Follow-up Modal */}
      <Dialog open={showFollowUpModal} onOpenChange={setShowFollowUpModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Schedule Follow-up for {clientCode}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleScheduleFollowUp} className="space-y-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <DMYDateInput
  value={followUpForm.follow_up_date}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, follow_up_date: e.target.value }))}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={followUpForm.follow_up_type} onValueChange={(v) => setFollowUpForm(prev => ({ ...prev, follow_up_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Phone Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="follow_up">General Follow-up</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={followUpForm.priority} onValueChange={(v) => setFollowUpForm(prev => ({ ...prev, priority: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={followUpForm.notes}
                onChange={(e) => setFollowUpForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="What do you want to discuss?"
                rows={3}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowFollowUpModal(false)}>Cancel</Button>
              <Button type="submit">
                <Calendar className="w-4 h-4 mr-1" /> Schedule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientTimelinePage;
